import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Store, ExternalLink, QrCode, MapPin, Copy, Tag } from '../../lib/lucide-proxy';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../config/supabase';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import toast from 'react-hot-toast';

const BreweryDashboard = () => {
  const { profile } = useAuth();
  const [brewery, setBrewery] = useState(null);
  const [keywords, setKeywords] = useState('');

  useEffect(() => {
    if (profile?.brewery) {
      setBrewery(profile.brewery);
      // Load additional brewery details if needed
    }
  }, [profile]);

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Link copiado!');
  };

  if (!brewery) return <div>Carregando dados da cervejaria...</div>;

  const publicLink = `${window.location.origin}/loja/${brewery.slug}`;
  const menuLink = `${window.location.origin}/menu/${brewery.slug}`;

  return (
    <>
      <Helmet>
        <title>Painel da Cervejaria - Cerveja Delivery</title>
      </Helmet>

      <div className="space-y-8">
        <div>
          <h2 className="text-2xl font-bold text-dark-900">Meu Negócio</h2>
          <p className="text-dark-600">Gerencie sua presença digital e links.</p>
        </div>

        {/* Business Card (Google My Business Style) */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-dark-900 h-32 relative">
            <div className="absolute -bottom-10 left-8">
              <img 
                src={brewery.logo || 'https://via.placeholder.com/100'} 
                alt="Logo" 
                className="w-24 h-24 rounded-xl border-4 border-white shadow-md object-cover" 
              />
            </div>
            <div className="absolute top-4 right-4">
              <span className={`px-3 py-1 rounded-full text-xs font-bold ${brewery.is_active ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                {brewery.is_active ? 'Loja Online' : 'Loja Offline'}
              </span>
            </div>
          </div>
          
          <div className="pt-12 pb-6 px-8">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-2xl font-bold text-dark-900">{brewery.name}</h3>
                <div className="flex items-center text-dark-500 mt-1">
                  <MapPin className="w-4 h-4 mr-1" />
                  <span className="text-sm">{brewery.address}</span>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => window.open(publicLink, '_blank')}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Ver Loja
              </Button>
            </div>

            {/* Quick Links Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold text-dark-900 flex items-center">
                    <Store className="w-4 h-4 mr-2 text-primary-500" /> Link da Loja
                  </h4>
                  <button onClick={() => copyLink(publicLink)} className="text-primary-600 hover:text-primary-700 text-xs font-medium flex items-center">
                    <Copy className="w-3 h-3 mr-1" /> Copiar
                  </button>
                </div>
                <p className="text-xs text-dark-500 mb-2">Link direto para sua página completa com carrinho.</p>
                <code className="block bg-white p-2 rounded border border-gray-200 text-xs text-dark-600 break-all">
                  {publicLink}
                </code>
              </div>

              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold text-dark-900 flex items-center">
                    <QrCode className="w-4 h-4 mr-2 text-primary-500" /> Menu Digital
                  </h4>
                  <button onClick={() => copyLink(menuLink)} className="text-primary-600 hover:text-primary-700 text-xs font-medium flex items-center">
                    <Copy className="w-3 h-3 mr-1" /> Copiar
                  </button>
                </div>
                <p className="text-xs text-dark-500 mb-2">Versão simplificada apenas para visualização (QR Code).</p>
                <code className="block bg-white p-2 rounded border border-gray-200 text-xs text-dark-600 break-all">
                  {menuLink}
                </code>
              </div>
            </div>

            {/* Keywords / SEO */}
            <div className="mt-8">
              <h4 className="font-bold text-dark-900 mb-3 flex items-center">
                <Tag className="w-4 h-4 mr-2 text-dark-500" /> Palavras-chave e Tags
              </h4>
              <div className="flex gap-2 flex-wrap mb-3">
                <span className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm">Cerveja Artesanal</span>
                <span className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm">Delivery</span>
                <span className="px-3 py-1 bg-primary-50 text-primary-700 rounded-full text-sm">IPA</span>
              </div>
              <p className="text-xs text-dark-500">
                Estas tags ajudam os usuários a encontrarem sua cervejaria na busca. <span className="text-primary-600 ml-1 cursor-pointer">Editar tags</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BreweryDashboard;